/**
 * «Оновити з інвертора» (D2): read the inverter's settings from DeyeCloud
 * again — the limits, the program — at most once in 10 minutes (DeyeCloud
 * limits sign-ins): the time of the next read beside it.
 */
import { useState } from 'react';
import type { SiteResponse } from 'shared';

import { useRefreshInverterSettings } from '../hooks/use-sites.js';
import { useLocale } from '../i18n/locale-context.js';
import type { ApiError } from '../lib/api-client.js';
import { useNow } from './charts/day-axis.js';
import { apiCode, errorText } from './errors.js';
import { fmtTime } from './format.js';

export function RefreshInverterSettings({ site }: { site: SiteResponse }) {
  const { t, tf } = useLocale();
  const refresh = useRefreshInverterSettings(site.id);
  const [done, setDone] = useState(false);
  const nowMs = useNow(15_000);
  // 429 `too_soon` carries the time the next read is possible.
  const retryAt =
    apiCode(refresh.error) === 'too_soon' ? (refresh.error as ApiError).details?.retry_at : null;
  const nextAt = typeof retryAt === 'string' ? retryAt : site.inverter_settings.next_read_at;
  const next = nextAt && Date.parse(nextAt) > nowMs ? nextAt : null;
  const failed = refresh.error
    ? apiCode(refresh.error) === 'access_not_open'
      ? t('site.prog.no_access')
      : apiCode(refresh.error) === 'too_soon'
        ? null
        : errorText(refresh.error, tf)
    : null;

  return (
    <div className="row-actions">
      <button
        type="button"
        className="btn"
        disabled={!!next || refresh.isPending}
        onClick={() => {
          setDone(false);
          refresh.mutate(undefined, { onSuccess: () => setDone(true) });
        }}
      >
        {t('site.prog.refresh')}
      </button>
      <span className="sub" aria-live="polite">
        {[
          failed ?? (done ? t('site.prog.done') : ''),
          next ? tf('site.prog.next', { time: fmtTime(next, site.timezone) }) : '',
        ]
          .filter(Boolean)
          .join(' ')}
      </span>
    </div>
  );
}
